import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { fetchCart } from "../../redux/features/cartSlice";
import { createStripeToken } from "../../redux/features/payStripeSlice";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import "./PayStripe.css"; // ✅ custom modern styles
import { useTranslation } from "react-i18next";

function PayStripe() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [cardNumber, setCardNumber] = useState("");
  const [expiryDate, setExpiryDate] = useState("");
  const [csv, setCSV] = useState("");
  const [cardType, setCardType] = useState("");
  const [errorMessages, setErrorMessages] = useState({});
  const { order_ids } = useParams();

  const { isValid, discount, status, code } = useSelector(
    (state) => state.coupon
  );

  const detectCardType = (number) => {
    const visaRegex = /^4/;
    const masterCardRegex = /^5[1-5]/;
    if (visaRegex.test(number)) return "Visa";
    if (masterCardRegex.test(number)) return "MasterCard";
    return "";
  };

  const handleCardNumberChange = (e) => {
    let value = e.target.value.replace(/\D/g, "").slice(0, 16);
    value = value.replace(/(\d{4})/g, "$1 ").trim();
    setCardNumber(value);
    setCardType(detectCardType(value.replace(/\s/g, "")));

    if (value.length < 19) {
      setErrorMessages((prev) => ({
        ...prev,
        cardNumber: "Card number must be 16 digits",
      }));
    } else {
      setErrorMessages((prev) => ({ ...prev, cardNumber: "" }));
    }
  };

  const handleExpiryDateChange = (e) => {
    let value = e.target.value.replace(/\D/g, "").slice(0, 4);
    if (value.length >= 2) value = value.slice(0, 2) + "/" + value.slice(2);
    setExpiryDate(value);

    if (!isExpiryValid(value)) {
      setErrorMessages((prev) => ({
        ...prev,
        expiryDate: "Invalid expiry date (MM/YY)",
      }));
    } else {
      setErrorMessages((prev) => ({ ...prev, expiryDate: "" }));
    }
  };

  const isExpiryValid = (date) => {
    if (!date.includes("/")) return false;
    const [month, year] = date.split("/").map(Number);
    const currentYear = new Date().getFullYear() % 100;
    const currentMonth = new Date().getMonth() + 1;
    return (
      month >= 1 &&
      month <= 12 &&
      year >= currentYear &&
      (year > currentYear || month >= currentMonth)
    );
  };

  const handleCSVChange = (e) => {
    let value = e.target.value.replace(/\D/g, "").slice(0, 3);
    setCSV(value);
    if (value.length < 3) {
      setErrorMessages((prev) => ({
        ...prev,
        csv: "CSV must be 3 digits",
      }));
    } else {
      setErrorMessages((prev) => ({ ...prev, csv: "" }));
    }
  };

  const { cartItems, totalAmount, deliveryCharges, couponDiscount, netAmount } =
    useSelector((state) => state.cart);
  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(fetchCart({ code }));
  }, [dispatch]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (
      errorMessages.cardNumber ||
      errorMessages.expiryDate ||
      errorMessages.csv
    )
      return;

    try {
      const resultAction = await dispatch(
        createStripeToken({
          cardNumber,
          expiryDate,
          csv,
          cardType,
          order_ids,
        })
      );

      if (createStripeToken.rejected.match(resultAction)) {
        toast.error(`${t("Payment_failed_Please_try_again")}.`, {
          position: "top-right",
          hideProgressBar: true,
          position: 'bottom-right',
          theme: 'dark',

          autoClose: 2000,
        });
      } else {
        toast.success(`${t("Order_placed_successfully!")}`, {
          position: "top-right",
          hideProgressBar: true,
          position: 'bottom-right',
          theme: 'dark',

          autoClose: 2000,
        });
        setTimeout(() => navigate("/order"), 2500);
      }
    } catch {
      toast.error(`${t("An_unexpected_error_occurred_Please_try_again")}.`, {
        position: "top-right",
        hideProgressBar: true,
        position: 'bottom-right',
        theme: 'dark',

        autoClose: 2000,
      });
    }
  };

  return (
    <section className="paystripe-section py-5">
      <div className="container">
        <div className="row g-4 justify-content-center">
          {/* Payment Form */}
          <div className="col-lg-7">
            <div className="payment-card p-4 shadow-lg rounded-4 glass-card">
              <h3 className="mb-4 fw-bold text-primary">💳 Enter Card Details</h3>
              <form className="row g-3" onSubmit={handleSubmit}>
                <div className="col-md-6">
                  <label className="form-label fw-semibold">
                    Card Number {cardType && `(${cardType})`}
                  </label>
                  <div className="position-relative">
                    <input
                      type="text"
                      className="form-control modern-input"
                      placeholder="1234 5678 9012 3456"
                      value={cardNumber}
                      onChange={handleCardNumberChange}
                      required
                    />
                    {cardType && (
                      <i
                        className={`card-icon fab fa-cc-${cardType.toLowerCase()}`}
                      ></i>
                    )}
                  </div>
                  {errorMessages.cardNumber && (
                    <small className="text-danger">
                      {errorMessages.cardNumber}
                    </small>
                  )}
                </div>

                <div className="col-md-3">
                  <label className="form-label fw-semibold">Expiry Date</label>
                  <input
                    type="text"
                    className="form-control modern-input"
                    placeholder="MM/YY"
                    value={expiryDate}
                    onChange={handleExpiryDateChange}
                    required
                  />
                  {errorMessages.expiryDate && (
                    <small className="text-danger">
                      {errorMessages.expiryDate}
                    </small>
                  )}
                </div>

                <div className="col-md-3">
                  <label className="form-label fw-semibold">CSV</label>
                  <input
                    type="text"
                    className="form-control modern-input"
                    placeholder="123"
                    value={csv}
                    onChange={handleCSVChange}
                    required
                  />
                  {errorMessages.csv && (
                    <small className="text-danger">{errorMessages.csv}</small>
                  )}
                </div>

                <div className="col-md-12 text-end mt-3">
                  <button className="btn btn-primary px-5 py-2">
                    Pay Now
                  </button>
                </div>
              </form>
            </div>
          </div>

          {/* Order Summary */}
          <div className="col-lg-5">
            <div className="order-summary shadow-lg rounded-4 p-4 bg-white">
              <h4 className="fw-bold mb-4">🧾 Billing Summary</h4>
              {cartItems.length > 0 ? (
                <>
                  {cartItems.map((item) => (
                    <div
                      key={item.id}
                      className="d-flex justify-content-between border-bottom py-2"
                    >
                      <div>
                        <strong>{item.title}</strong> x{item.quantity}
                        {item.combinations?.length > 0 && (
                          <div className="small text-muted">
                            {item.combinations
                              .filter((combo) => combo.key !== "1")
                              .map((combo, i) => (
                                <div key={i}>
                                  {combo.key}: {combo.value}
                                </div>
                              ))}
                          </div>
                        )}
                        <small className="text-muted">
                          {t("Estimated_Delivery")}:{" "}
                          {new Date(
                            Date.now() + (item?.delivered_day || 2) * 86400000
                          ).toLocaleDateString("en-US", {
                            weekday: "long",
                            month: "long",
                            day: "2-digit",
                          })}
                        </small>
                      </div>
                      <div>€{(item.sales_price * item.quantity).toFixed(2)}</div>
                    </div>
                  ))}
                  <div className="mt-3">
                    <div className="d-flex justify-content-between">
                      <span>Subtotal</span>
                      <strong>€{totalAmount.toFixed(2)}</strong>
                    </div>
                    <div className="d-flex justify-content-between">
                      <span>Shipping</span>
                      <strong>
                        {deliveryCharges > 0
                          ? `€${deliveryCharges.toFixed(2)}`
                          : "Free"}
                      </strong>
                    </div>
                    {couponDiscount > 0 && (
                      <div className="d-flex justify-content-between text-success">
                        <span>Discount</span>
                        <strong>-€{couponDiscount}</strong>
                      </div>
                    )}
                    <hr />
                    <div className="d-flex justify-content-between fs-5 fw-bold text-primary">
                      <span>Total</span>
                      <span>€{netAmount.toFixed(2)}</span>
                    </div>
                  </div>
                </>
              ) : (
                <p className="text-muted">No items in cart</p>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

export default PayStripe;
